import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { createVirtualAccount } from '../../services/api';
import { truncateText } from '../../utils/helpers';

const VirtualAccountModal = ({ open, onClose, linkedBrokers, onSuccess }) => {
  const [name, setName] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [sourceAccountId, setSourceAccountId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  

  const ibAccounts = linkedBrokers.filter(account => account.broker === 'ib');
  

  useEffect(() => {
    if (open) {
      setName('');
      setSelectedAccounts([]);
      setSourceAccountId(null);
      setError('');
    }
  }, [open]);
  

  useEffect(() => {

    if (sourceAccountId && !selectedAccounts.includes(sourceAccountId)) {
      setSourceAccountId(null);
    }
    

    if (!sourceAccountId && selectedAccounts.length > 0) {
      setSourceAccountId(selectedAccounts[0]);
    }
  }, [selectedAccounts, sourceAccountId]);
  
  const handleAccountSelect = (accountId) => {
    if (selectedAccounts.includes(accountId)) {

      setSelectedAccounts(selectedAccounts.filter(id => id !== accountId));
    } else {

      setSelectedAccounts([...selectedAccounts, accountId]);
    }
  };
  
  const handleSourceSelect = (accountId) => {

    if (selectedAccounts.includes(accountId)) {
      setSourceAccountId(accountId);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    

    if (!name.trim()) {
      setError('Please enter a name for the virtual account');
      return;
    }
    
    if (selectedAccounts.length === 0) {
      setError('Please select at least one account');
      return;
    }
    
    if (!sourceAccountId) {
      setError('Please designate a source account');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await createVirtualAccount(name, selectedAccounts, sourceAccountId);
      if (result && result.success) {
        onClose();
        if (onSuccess) onSuccess(result);
      } else {
        setError('Failed to create virtual account');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Modal open={open} onClose={onClose}>
      <div className="w-full max-w-md p-4">
        <h2 className="text-xl font-bold mb-4 text-center">Create Virtual Account</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <p className="text-gray-600 mb-4">
          Virtual accounts let you synchronize trading settings between multiple accounts. 
          Select a source account that will be used to sync settings to other accounts.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block mb-1 font-medium">Virtual Account Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="My Virtual Account"
              required
            />
          </div>
          
          <div>
            <label className="block mb-1 font-medium">Select Accounts to Link</label>
            {ibAccounts.length > 0 ? (
              <div className="border rounded-md divide-y">
                {ibAccounts.map((account) => {
                  const isSelected = selectedAccounts.includes(account.record_id);
                  const isSource = sourceAccountId === account.record_id;
                  
                  return (
                    <div key={account.record_id} className="p-3 flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`account-${account.record_id}`}
                          checked={isSelected}
                          onChange={() => handleAccountSelect(account.record_id)}
                          className="mr-2"
                        />
                        <label htmlFor={`account-${account.record_id}`} className="flex items-center">
                          <span>{truncateText(account.account, 20)}</span>
                        </label>
                      </div>
                      
                      <div>
                        <input
                          type="radio"
                          name="sourceAccount"
                          id={`source-${account.record_id}`}
                          checked={isSource}
                          onChange={() => handleSourceSelect(account.record_id)}
                          disabled={!isSelected}
                          className="mr-2"
                        />
                        <label 
                          htmlFor={`source-${account.record_id}`}
                          className={`${!isSelected ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          Source
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 border rounded-md bg-gray-50">
                <p className="text-gray-500">No IB accounts available</p>
                <p className="text-gray-500 text-sm">Please link an IB account first</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedAccounts.length === 0 || !sourceAccountId}
              className={`px-4 py-2 rounded-md text-white ${
                loading || selectedAccounts.length === 0 || !sourceAccountId
                  ? 'bg-blue-300'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {loading ? 'Creating...' : 'Create Virtual Account'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default VirtualAccountModal;